import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Youtube, Linkedin, Mail, TrendingUp, BookOpen, Mic, GraduationCap, DollarSign, FileText, Phone, Home, Headphones, Play, School } from 'lucide-react';

const navLinks = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'Investors', path: '/investors', icon: TrendingUp },
  { name: 'IFC', path: '/ifc', icon: GraduationCap },
  { name: 'CIRO', path: '/ciro', icon: DollarSign },
  { name: 'Courses', path: '/courses', icon: BookOpen },
  { name: 'YouTube', path: '/youtube', icon: Play },
  { name: 'Investment Academy', path: '/investment-academy', icon: School, external: true },
  { name: 'Podcasts', path: '/podcasts', icon: Mic },
  { name: 'Audio Books', path: '/audiobooks', icon: Headphones },
  { name: 'Blog', path: '/blog', icon: FileText },
  { name: 'Contact', path: '/contact', icon: Phone },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group" data-testid="nav-logo">
            <span className="font-heading text-xl md:text-2xl font-bold text-white group-hover:text-amber-400 transition-colors">
              Harry Vadalkar
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.slice(0, 7).map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className="px-3 py-2 text-sm font-medium rounded-lg transition-all text-slate-300 hover:text-white hover:bg-slate-800/50"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\//g, '-')}`}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    location.pathname === link.path
                      ? 'text-blue-400 bg-blue-500/10'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            {/* More dropdown for remaining links */}
            <div className="relative group">
              <button className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/50 transition-all">
                More
              </button>
              <div className="absolute right-0 mt-1 w-48 py-2 glass rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {navLinks.slice(7).map((link) => (
                  link.external ? (
                    <a
                      key={link.path}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                      className="flex items-center px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50"
                    >
                      <link.icon className="w-4 h-4 mr-2" />
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                      className="flex items-center px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50"
                    >
                      <link.icon className="w-4 h-4 mr-2" />
                      {link.name}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Link
              to="/courses"
              data-testid="nav-cta-courses"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2 rounded-full transition-all shadow-lg hover:shadow-blue-500/30"
            >
              View Courses
            </Link>
            <a
              href="https://www.amazon.ca/Wealthy-Canadian-hilarious-heartfelt-journey-ebook/dp/B0FDDBRKV7"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-book"
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-5 py-2 rounded-full transition-all shadow-lg hover:shadow-amber-500/30"
            >
              Get the Book
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/50"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden glass border-t border-slate-800" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center px-4 py-3 rounded-lg transition-all text-slate-300 hover:text-white hover:bg-slate-800/50"
                >
                  <link.icon className="w-5 h-5 mr-3" />
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                    location.pathname === link.path
                      ? 'text-blue-400 bg-blue-500/10'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <link.icon className="w-5 h-5 mr-3" />
                  {link.name}
                </Link>
              )
            ))}
            <a
              href="https://www.amazon.ca/Wealthy-Canadian-hilarious-heartfelt-journey-ebook/dp/B0FDDBRKV7"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center mt-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold px-5 py-3 rounded-full"
            >
              Get the Book
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900/50 border-t border-slate-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <h3 className="font-heading text-2xl font-bold text-white mb-4">Harry Vadalkar</h3>
            <p className="text-slate-400 mb-4 max-w-md">
              Harvard & MIT certified financial educator, CFA, CSC, MBA. Expert in Canadian investing, CSC exam prep, IFC certification, and CIRO training. Author of Wealthy Canadian.
            </p>
            <p className="text-slate-500 text-sm mb-6 max-w-md">
              Teaching at SAIT & Mount Royal University. Helping 30,000+ students master stock market investing, TFSA strategies, RRSP planning, mutual funds, and ETF investing in Canada.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.youtube.com/@FinanceinCanada"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                aria-label="Finance in Canada YouTube Channel"
                className="p-2 rounded-lg bg-slate-800 hover:bg-red-600 text-slate-400 hover:text-white transition-all"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/in/herambvadalkar/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                aria-label="Harry Vadalkar LinkedIn Profile"
                className="p-2 rounded-lg bg-slate-800 hover:bg-blue-600 text-slate-400 hover:text-white transition-all"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <Link
                to="/contact"
                data-testid="footer-contact"
                aria-label="Contact Harry Vadalkar"
                className="p-2 rounded-lg bg-slate-800 hover:bg-amber-500 text-slate-400 hover:text-black transition-all"
              >
                <Mail className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Courses & Books</h4>
            <ul className="space-y-2">
              {navLinks.slice(0, 5).map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold text-white mb-4">Resources</h4>
            <ul className="space-y-2">
              {navLinks.slice(5).map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Harry Vadalkar. All rights reserved.
          </p>
          <p className="text-slate-600 text-sm mt-2 md:mt-0">
            Harvard & MIT Certified | CFA | CSC | MBA
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#020617]">
      <Navbar />
      <main className="pt-16 md:pt-20">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;